import { XIcon } from 'lucide-react'
import { Dialog as SheetPrimitive } from 'radix-ui'
import * as React from 'react'

import { Button } from '@/src/designSystem/components/Button'
import { Input } from '@/src/designSystem/components/Input'
import { cn } from '@/src/designSystem/lib/utils'

const sheetContentWidthClasses = {
  small: 'data-[side=left]:sm:max-w-sm data-[side=right]:sm:max-w-sm',
  medium: 'data-[side=left]:sm:max-w-md data-[side=right]:sm:max-w-md',
  large: 'data-[side=left]:sm:max-w-lg data-[side=right]:sm:max-w-lg',
} as const

const sheetInsetClassName =
  'data-[side=left]:inset-y-2 data-[side=left]:left-2 data-[side=left]:h-auto data-[side=left]:max-h-[calc(var(--visual-viewport-height)-1rem)] data-[side=left]:rounded-lg data-[side=left]:border data-[side=left]:border-border data-[side=right]:inset-y-2 data-[side=right]:right-2 data-[side=right]:h-auto data-[side=right]:max-h-[calc(var(--visual-viewport-height)-1rem)] data-[side=right]:rounded-lg data-[side=right]:border data-[side=right]:border-border'

export type SheetContentWidth = keyof typeof sheetContentWidthClasses

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot='sheet' {...props} />
}

function SheetTrigger({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot='sheet-trigger' {...props} />
}

function SheetClose({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot='sheet-close' {...props} />
}

function SheetPortal({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot='sheet-portal' {...props} />
}

function SheetOverlay({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
  return (
    <SheetPrimitive.Overlay
      data-slot='sheet-overlay'
      className={cn('fixed inset-0 z-50 bg-black/40', className)}
      {...props}
    />
  )
}

function SheetContent({
  className,
  children,
  side = 'right',
  width = 'small',
  inset = false,
  showCloseButton = true,
  showOverlay = false,
  overlayClassName,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: 'top' | 'right' | 'bottom' | 'left'
  width?: SheetContentWidth
  /** Floats left/right sheets inside the viewport instead of docking to the edge. */
  inset?: boolean
  showCloseButton?: boolean
  showOverlay?: boolean
  overlayClassName?: string
}) {
  return (
    <SheetPortal>
      {showOverlay && <SheetOverlay className={overlayClassName} />}
      <SheetPrimitive.Content
        data-slot='sheet-content'
        data-side={side}
        className={cn(
          'fixed z-50 flex flex-col bg-background bg-clip-padding text-xs/relaxed shadow-lg transition duration-200 ease-in-out data-[side=bottom]:inset-x-0 data-[side=bottom]:bottom-0 data-[side=bottom]:h-auto data-[side=bottom]:border-t data-[side=left]:inset-y-0 data-[side=left]:left-0 data-[side=left]:h-full data-[side=left]:w-3/4 data-[side=left]:border-r data-[side=right]:inset-y-0 data-[side=right]:right-0 data-[side=right]:h-full data-[side=right]:w-3/4 data-[side=right]:border-l data-[side=top]:inset-x-0 data-[side=top]:top-0 data-[side=top]:h-auto data-[side=top]:border-b ',
          sheetContentWidthClasses[width],
          inset && sheetInsetClassName,
          className
        )}
        {...props}>
        {children}
        {showCloseButton && (
          <SheetPrimitive.Close data-slot='sheet-close' asChild>
            <Button
              variant='ghost'
              className='absolute top-4 right-4'
              size='icon-sm'>
              <XIcon />
              <span className='sr-only'>Close</span>
            </Button>
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Content>
    </SheetPortal>
  )
}

function SheetHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='sheet-header'
      className={cn('flex flex-col gap-1.5 p-6', className)}
      {...props}
    />
  )
}

function SheetFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='sheet-footer'
      className={cn('mt-auto flex flex-col gap-2 p-6', className)}
      {...props}
    />
  )
}

function SheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      data-slot='sheet-title'
      className={cn('text-sm font-medium text-foreground', className)}
      {...props}
    />
  )
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot='sheet-description'
      className={cn('text-xs/relaxed text-muted-foreground', className)}
      {...props}
    />
  )
}

function SheetEditableTitle({
  title,
  onChange,
}: {
  title: string
  onChange: (title: string) => void
}) {
  const [isEditingTitle, setIsEditingTitle] = React.useState(false)
  const [titleValue, setTitleValue] = React.useState(title)

  const saveTitle = () => {
    const trimmed = titleValue.trim()
    if (trimmed && trimmed !== title) {
      onChange(trimmed)
    } else {
      setTitleValue(title)
    }
    setIsEditingTitle(false)
  }

  return (
    <>
      {isEditingTitle ? (
        <Input
          autoFocus
          value={titleValue}
          onChange={(e) => setTitleValue(e.target.value)}
          onBlur={saveTitle}
          onKeyDown={(e) => {
            if (e.key === 'Enter') saveTitle()
            if (e.key === 'Escape') {
              setTitleValue(title)
              setIsEditingTitle(false)
            }
          }}
          className='text-base font-semibold leading-tight h-auto py-1 px-2 -mx-2'
        />
      ) : (
        <h2
          className='text-base font-semibold leading-tight cursor-text rounded-sm hover:bg-muted -mx-1 px-1 py-0.5 transition-colors mb-2'
          onClick={() => setIsEditingTitle(true)}>
          {title}
        </h2>
      )}
    </>
  )
}

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetEditableTitle,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
}
